import { Box, Checkbox, ClickAwayListener, InputBase, Paper, Popper, TextField } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TreeUtils } from '../../../utils/TreeUtils';
import { PeriodicityButton } from './PeriodicityButton';

export const DimTree = ({
  displayName,     // Label of the TreeSelect
  disabled,        // Whether this tree is disabled. TODO: Disabled should still be able to open dropdown
  treeData,        // Array of objects containing id, name, and children
  selectedIds,     // Array of selected IDs (leaf nodes only)
  onFilterChange,  // A function that will be called when the user changes the values. This component will pass the new selected leaf IDs into the function

  // Optional for dimensions that have Periodicities - These are simply passed through to PeriodicityButton
  periodicities,          // A list of the periodicities for this dimension
  selectedPeriodicities,  // A list of the periodicities that are currently selected
  onPeriodicityChange,    // A function that will be called when the user changes the periodicities. This component will pass the new periodicities into the function
}) => {

  // ----- State -----

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState([]);
  const anchorRef = useRef(null);

  // ----- Initialization -----

  const { idToNode, leafIds } = useMemo(() => {
    return TreeUtils.parseTree(treeData)
  }, [treeData]);

  // On initialization, the root node should be expanded
  useEffect(() => {
    const rootId = treeData.id;
    setExpanded((prev) => (prev.length === 0 ? [rootId] : prev));
  }, [treeData]);

  // ----- Selection -----

  const descendantLeafIdsFromId = (id) => {
    const node = idToNode[id];
    if (node.children?.length > 0) {
      const descendantLeafIds = [];
      node.children.forEach(childNode => {
        descendantLeafIds.push(...descendantLeafIdsFromId(childNode.id));
      })
      return descendantLeafIds;
    } else {
      return [id];
    }
  }

  const isFullySelected = (id) => {
    const descendantLeafIds = descendantLeafIdsFromId(id);
    return descendantLeafIds.every(id => selectedIds.includes(id));
  }

  const isPartiallySelected = (id) => {
    const descendantLeafIds = descendantLeafIdsFromId(id);
    return !descendantLeafIds.every(id => selectedIds.includes(id)) && descendantLeafIds.some(id => selectedIds.includes(id));
  }

  // Calculates new set of selectedIds and calls onChange (prop from parent)
  const handleCheckboxClick = (id) => {
    const newSelectedIds = [...selectedIds];

    // Determine the target leaves for this toggle
    const targetIds = leafIds.includes(id) ? [id] : descendantLeafIdsFromId(id);

    // If all descendant leaves are already selected, then we are removing
    const removing = targetIds.every((targetId) => selectedIds.includes(targetId));

    targetIds.forEach((leafId) => {
      if (removing) {
        newSelectedIds.splice(newSelectedIds.indexOf(leafId), 1);
      } else if (newSelectedIds.indexOf(leafId) === -1) {
        newSelectedIds.push(leafId);
      }
    });

    onFilterChange(newSelectedIds);
  };

  // ----- Rendering -----

  // Returns the tree that will that will be rendered after filtering the nodes by the search
  // This function also fills out the array of autoExpandIds
  const buildVisibleTree = (nodes, search, autoExpandedIds) => {
    const lowerCaseSearch = search.toLowerCase();
    return nodes.reduce((accumulated, node) => {
      const selfHit = node.name.toLowerCase().includes(lowerCaseSearch);
      const children = node.children ? buildVisibleTree(node.children, search, autoExpandedIds) : [];
      if (selfHit || children.length) {
        if (search) autoExpandedIds.push(node.id);
        accumulated.push({ ...node, children: selfHit ? node.children ?? [] : children });
      }
      return accumulated;
    }, []);
  };

  const autoExpandedIds = [];
  const visibleTree = buildVisibleTree([treeData], search, autoExpandedIds);
  const expandedIds = search ? Array.from(new Set([...expanded, ...autoExpandedIds])) : expanded;

  // Recursively render nodes of the tree
  const renderItems = (nodes) => {
    return nodes.map((node) => {
      const hasChildren = !!node.children?.length;
      const checked = hasChildren ? isFullySelected(node.id) : selectedIds.includes(node.id);
      const indeterminate = hasChildren && isPartiallySelected(node.id);
      return (
        <TreeItem
          key={node.id}
          itemId={node.id}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={checked}
                indeterminate={indeterminate}
                onClick={(event) => event.stopPropagation()}
                onChange={() => handleCheckboxClick(node.id)}
              />
              {node.name}
            </Box>
          }
        >
          {hasChildren && renderItems(node.children)}
        </TreeItem>
      );
    });
  }

  // The text that is displayed in the TextField is dependent on what nodes are selected
  let textFieldContent = '';
  if (selectedIds.length === leafIds.length) {
    textFieldContent = 'All'
  } else if (selectedIds.length === 0) {
    textFieldContent = 'None';
  } else if (selectedIds.length <= 3) {
    textFieldContent = selectedIds.map((id) => idToNode[id].name).join(', ')
  } else {
    textFieldContent = `${selectedIds.length} selected`
  }

  return (
    <>
      <TextField
        ref={anchorRef}
        fullWidth
        variant="outlined"
        label={displayName}
        value={textFieldContent}
        onClick={() => setOpen((prev) => !prev)}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              periodicities &&
              <PeriodicityButton
                periodicities={periodicities}
                selectedPeriodicities={selectedPeriodicities}
                onPeriodicityChange={onPeriodicityChange}
                closeFilterDropdown={() => setOpen(false)}
                anchorRef={anchorRef}
                dimDisplayName={displayName}
              />
            )
          }
        }}
        disabled={disabled}
      />

      <Popper
        open={open && !disabled}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper sx={{ width: anchorRef.current?.clientWidth || 300, p: 1, maxHeight: 600, overflow: 'auto' }}>
            {/* Search */}
            <Box sx={{ mb: 1, px: 1, py: 0.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
              <InputBase
                fullWidth
                placeholder="Search…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                disabled={disabled}
              />
            </Box>

            {/* Tree */}
            <SimpleTreeView
              expandedItems={expandedIds}
              onExpandedItemsChange={(event, ids) => setExpanded(ids)}
            >
              {renderItems(visibleTree)}
            </SimpleTreeView>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
